/**
 * Admin API Client
 *
 * Provides typed HTTP client methods for all admin dashboard API endpoints.
 * Uses the standard apiClient which automatically includes JWT authentication.
 */

import { apiClient } from '../api/client.js';
import type {
  DashboardStatsResponse,
  CollectionDetailResponse,
  RecentJobsResponse,
  TriggerCollectionIndexingRequest,
  TriggerCollectionIndexingResponse,
  TriggerOfferSyncRequest,
  TriggerOfferSyncResponse,
  TriggerHistoryFetchRequest,
  TriggerHistoryFetchResponse,
  ErrorResponse,
  RarityMethodology,
  ValuationStrategy,
  AdminUsersListResponse,
  AdminUserDetailResponse,
  CreateCollectionRequest,
  CreateCollectionResponse,
  CollectionsListResponse,
  MarketplaceStatsResponse,
  AdminTradesListResponse,
  AdminTradeStats,
  AdminTradeDetailResponse,
} from '../types/admin.js';

// ==================== Dashboard Statistics ====================

export async function getDashboardStats(): Promise<DashboardStatsResponse> {
  return apiClient.get<DashboardStatsResponse>('/admin/dashboard/stats');
}

// ==================== Analytics Health ====================

export interface AnalyticsHealthResponse {
  success: boolean;
  data: {
    status: 'healthy' | 'degraded' | 'stale';
    unprocessed: {
      total: number;
      recent7d: number;
      salesCount: number;
      missingVolumeXrp: number;
    };
    lastProcessedAt: string | null;
    minutesSinceProcessed: number | null;
    processedLastHour: number;
    latestAnalyticsBucket: string | null;
    recentUnprocessedByHour: Array<{
      hour: string;
      txCount: number;
      sales: number;
      volumeXrp: number;
    }>;
  };
}

export interface ReprocessResponse {
  success: boolean;
  data: {
    message: string;
    reprocessedCount: number;
    bucketsUpdated: number;
  };
}

export async function getAnalyticsHealth(): Promise<AnalyticsHealthResponse> {
  return apiClient.get<AnalyticsHealthResponse>('/admin/analytics-health');
}

export async function reprocessAnalytics(): Promise<ReprocessResponse> {
  return apiClient.post<ReprocessResponse>('/admin/analytics-health/reprocess', {});
}

// ==================== Collections ====================

/**
 * Get all collections with summary information.
 */
export async function getCollections(params?: {
  sort?: 'name' | 'creator_address' | 'created_at' | 'total_supply' | 'floor_price';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  search?: string;
}): Promise<CollectionsListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.sort) queryParams.append('sort', params.sort);
  if (params?.order) queryParams.append('order', params.order);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.offset) queryParams.append('offset', params.offset.toString());
  if (params?.search) queryParams.append('search', params.search);

  const query = queryParams.toString();
  const endpoint = `/admin/collections${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{ success: boolean; data: CollectionsListResponse }>(endpoint);
  return response.data;
}

export async function getCollectionDetail(
  issuer: string,
  taxon: number | null
): Promise<CollectionDetailResponse> {
  // Use 'all' for null taxon (issuer-wide collections)
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.get<CollectionDetailResponse>(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}`
  );
}

// ==================== Recent Jobs ====================

export async function getRecentJobs(params?: {
  limit?: number;
  status?: 'queued' | 'processing' | 'completed' | 'failed';
}): Promise<RecentJobsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);

  const query = queryParams.toString();
  const endpoint = `/admin/jobs/recent${query ? `?${query}` : ''}`;

  return apiClient.get<RecentJobsResponse>(endpoint);
}

// ==================== Job Triggers ====================

export async function triggerCollectionIndexing(
  request: TriggerCollectionIndexingRequest
): Promise<TriggerCollectionIndexingResponse> {
  return apiClient.post<TriggerCollectionIndexingResponse>('/admin/jobs/trigger/indexing', request);
}

export async function triggerOfferSync(
  request: TriggerOfferSyncRequest
): Promise<TriggerOfferSyncResponse> {
  return apiClient.post<TriggerOfferSyncResponse>('/admin/jobs/trigger/offers', request);
}

export async function triggerHistoryFetch(
  request: TriggerHistoryFetchRequest
): Promise<TriggerHistoryFetchResponse> {
  return apiClient.post<TriggerHistoryFetchResponse>('/admin/jobs/trigger/history', request);
}

export async function triggerStatsRefresh(
  issuer: string,
  taxon: number | null
): Promise<{ message: string; collectionId: string; timestamp: string }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.post(`/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/refresh-stats`);
}

export async function updateRarityMethodology(
  issuer: string,
  taxon: number | null,
  methodology: RarityMethodology,
  triggerRecalculation: boolean = true
): Promise<{ message: string; collectionId: string; methodology: RarityMethodology; recalculationTriggered: boolean }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.patch(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/rarity-methodology`,
    { methodology, triggerRecalculation }
  );
}

export async function updateValuationStrategy(
  issuer: string,
  taxon: number | null,
  strategy: ValuationStrategy,
  triggerRecalculation: boolean = true
): Promise<{ message: string; collectionId: string; newStrategy: ValuationStrategy; recalculationTriggered: boolean; note: string }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.patch(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/valuation-strategy`,
    { strategy, triggerRecalculation }
  );
}

export async function updateRuggedStatus(
  issuer: string,
  taxon: number | null,
  isRugged: boolean,
  triggerRecalculation: boolean = true
): Promise<{ message: string; collectionId: string; wasRugged: boolean; isRugged: boolean; recalculationTriggered: boolean; note: string }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.patch(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/rugged-status`,
    { isRugged, triggerRecalculation }
  );
}

export async function triggerValuationRecalculation(
  issuer: string,
  taxon: number | null
): Promise<{ message: string; collectionId: string; collectionName: string; currentStrategy: ValuationStrategy; totalSupply: number; note: string; timestamp: string }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.post(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/trigger-valuation`
  );
}

/**
 * Update the XRPL VoteBoard ID for a collection.
 * This ID is used to sync community voting scores from the VoteBoard API.
 */
export async function updateVoteboardId(
  issuer: string,
  taxon: number | null,
  voteboardId: number | null
): Promise<{ message: string; collectionId: string; previousVoteboardId: number | null; newVoteboardId: number | null }> {
  const taxonParam = taxon !== null ? taxon : 'all';
  return apiClient.patch(
    `/admin/collections/${encodeURIComponent(issuer)}/${taxonParam}/voteboard-id`,
    { xrpl_voteboard_id: voteboardId }
  );
}

// ==================== Collection Management ====================

/**
 * Create a new collection with all required fields.
 * This directly creates the collection and triggers an indexing job.
 */
export async function createCollection(
  request: CreateCollectionRequest
): Promise<CreateCollectionResponse> {
  // Backend returns { success, message, collection, indexingJob } directly (no data wrapper)
  return apiClient.post<CreateCollectionResponse>(
    '/admin/collections',
    request
  );
}

/**
 * Upload collection image (profile or banner)
 */
export async function uploadCollectionImage(
  collectionId: string,
  file: File,
  type: 'profile' | 'banner'
): Promise<{ url: string; type: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/admin/collections/${encodeURIComponent(collectionId)}/upload-image?type=${type}`,
    {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || 'Failed to upload image');
  }

  const data = await response.json();
  return data.data;
}

// ==================== User Management ====================

export async function getAdminUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  sort?: 'created_at' | 'username' | 'xrpl_address' | 'email';
  order?: 'asc' | 'desc';
}): Promise<AdminUsersListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.sort) queryParams.append('sort', params.sort);
  if (params?.order) queryParams.append('order', params.order);

  const query = queryParams.toString();
  const endpoint = `/admin/users${query ? `?${query}` : ''}`;

  return apiClient.get<AdminUsersListResponse>(endpoint);
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetailResponse> {
  return apiClient.get<AdminUserDetailResponse>(`/admin/users/${encodeURIComponent(userId)}`);
}

// ==================== XRPL Connection Management ====================

export interface XrplResetResponse {
  success: boolean;
  message: string;
  data?: {
    previousServer: string;
    newServer: string;
    resetDurationMs: number;
    subscriptionsRestored: string[];
    connectionStatus?: {
      connected: boolean;
      server: string;
      subscriptionHealthy: boolean;
    };
  };
  timestamp: string;
}

export interface XrplStatusResponse {
  success: boolean;
  data: {
    connected: boolean;
    currentServer: string;
    reconnectAttempts: number;
    maxReconnectAttempts: number;
    lastLedgerEventTime: string | null;
    lastLedgerIndex: number | null;
    subscriptionHealthy: boolean;
    timeSinceLastLedgerMs: number | null;
    timeSinceLastLedgerFormatted: string | null;
  };
  timestamp: string;
}

/**
 * Triggers a full reset of the XRPL connection in the ingestion service.
 * This completely tears down and rebuilds the XRPL WebSocket connection.
 */
export async function resetXrplConnection(): Promise<XrplResetResponse> {
  return apiClient.post<XrplResetResponse>('/admin/xrpl/reset');
}

/**
 * Gets detailed XRPL connection status from the ingestion service.
 */
export async function getXrplStatus(): Promise<XrplStatusResponse> {
  return apiClient.get<XrplStatusResponse>('/admin/xrpl/status');
}

// ==================== Self-Healing (Enrichment Service) ====================

export interface SelfHealingStats {
  lastRun: {
    at: string | null;
    durationMs: number;
    recovered: {
      indexingJobs: number;
      orphanedNFTs: number;
      stuckMetadata: number;
      stuckMetadataFetched: number;
      stuckMedia: number;
      cleanedDuplicateJobs: number;
      markedPermanentFailures: number;
      markedNoMetadataUri: number;
    };
    errors: string[];
  };
  currentStuckCounts: {
    indexingJobs: number;
    nfts: {
      orphaned: number;
      stuckMetadata: number;
      stuckMetadataFetched: number;
      stuckMedia: number;
      noMetadataUri: number;
      permanentFailures: number;
    };
    redis: {
      metadataQueued: number;
      mediaQueued: number;
    };
  };
  timestamp: string;
}

export interface SelfHealingRunResponse {
  status: string;
  message: string;
  traceId: string;
  triggeredAt: string;
}

/**
 * Gets self-healing statistics from the enrichment service.
 * Shows last run info and current stuck counts.
 */
export async function getSelfHealingStats(): Promise<SelfHealingStats> {
  return apiClient.get<SelfHealingStats>('/admin/self-healing/stats');
}

/**
 * Triggers a manual self-healing run in the enrichment service.
 * Returns immediately with accepted status.
 */
export async function triggerSelfHealing(): Promise<SelfHealingRunResponse> {
  return apiClient.post<SelfHealingRunResponse>('/admin/self-healing/run');
}

// ==================== Project Management ====================

/**
 * Richlist points strategy enum values
 * These must match the values in shared/models/project.ts
 */
export const RICHLIST_STRATEGIES = {
  QUARTER_POINT_PER_NFT: '0.25_points_per_nft',
  HALF_POINT_PER_NFT: '0.5_points_per_nft',
  ONE_POINT_PER_NFT: '1_point_per_nft',
  TWO_POINTS_PER_NFT: '2_points_per_nft',
  TWO_AND_HALF_POINTS_PER_NFT: '2.5_points_per_nft',
  THREE_POINTS_PER_NFT: '3_points_per_nft',
  THREE_AND_QUARTER_POINTS_PER_NFT: '3.25_points_per_nft',
  FOUR_POINTS_PER_NFT: '4_points_per_nft',
  FIVE_POINTS_PER_NFT: '5_points_per_nft',
  SIX_POINTS_PER_NFT: '6_points_per_nft',
  SEVEN_POINTS_PER_NFT: '7_points_per_nft',
  EIGHT_POINTS_PER_NFT: '8_points_per_nft',
  NINE_POINTS_PER_NFT: '9_points_per_nft',
  TEN_POINTS_PER_NFT: '10_points_per_nft',
  HUNDRED_POINTS_PER_NFT: '100_points_per_nft',
  WISE_GUYS: 'wise_guys',
  RASCALZ: 'rascalz',
  VOC: 'voc',
  PLACERS: 'placers',
  BMD_HORSE_HEROES: 'bmd_horse_heroes',
  CONSPIRACY_WHITE_RABBIT: 'conspiracy_white_rabbit',
  CONSPIRACY_AREA_589: 'conspiracy_area_589',
} as const;

export type RichlistPointsStrategy = typeof RICHLIST_STRATEGIES[keyof typeof RICHLIST_STRATEGIES];

export interface CollectionConfig {
  richlistPointsStrategy?: RichlistPointsStrategy;
}

export type CollectionsConfig = Record<string, CollectionConfig>;

export type ProjectFeature = 'project_richlist' | 'project_rewards' | 'ambassador_program';

export interface AdminProject {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  website_url: string | null;
  twitter_url: string | null;
  discord_url: string | null;
  image_url: string | null;
  banner_url: string | null;
  owner_address: string | null;
  collections_config?: CollectionsConfig;
  enabled_features?: ProjectFeature[];
  created_at: string;
  updated_at: string;
  tags?: AdminProjectTag[];
  tokens?: AdminProjectToken[];
  collections?: AdminProjectCollection[];
  collection_count?: number;
  total_floor_value?: number;
}

export interface AdminProjectTag {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  created_at: string;
}

export interface AdminProjectToken {
  id: string;
  project_id: string;
  currency_code: string;
  issuer_address: string;
  display_name: string | null;
  price_xrp: number | null;
  price_change_24h: number | null;
  total_supply: number | null;
  circulating_supply: number | null;
  market_cap_xrp: number | null;
  has_liquidity: boolean;
  sort_order: number;
  last_fetched_at: string | null;
  created_at: string;
}

export interface AdminProjectCollection {
  id: string;
  name: string;
  slug: string;
  issuer_address: string;
  taxon: number | null;
  total_supply: number;
  floor_price_xrp: number | null;
}

export interface CreateProjectRequest {
  name: string;
  slug?: string;
  description?: string | null;
  website_url?: string | null;
  twitter_url?: string | null;
  discord_url?: string | null;
  image_url?: string | null;
  banner_url?: string | null;
  owner_address?: string | null;
  tag_ids?: string[];
}

export interface UpdateProjectRequest {
  name?: string;
  slug?: string;
  description?: string | null;
  website_url?: string | null;
  twitter_url?: string | null;
  discord_url?: string | null;
  image_url?: string | null;
  banner_url?: string | null;
  owner_address?: string | null;
  tag_ids?: string[];
  collections_config?: CollectionsConfig;
  enabled_features?: ProjectFeature[];
}

export interface AddTokenRequest {
  currency_code: string;
  issuer_address: string;
  display_name?: string | null;
  sort_order?: number;
}

export interface CreateTagRequest {
  name: string;
  slug?: string;
  sort_order?: number;
}

export interface ProjectDropdownItem {
  id: string;
  name: string;
  slug: string;
}

export interface UnassignedCollection {
  id: string;
  name: string;
  slug: string;
  issuer_address: string;
  taxon: number | null;
}

/**
 * Get all projects for admin
 */
export async function getAdminProjects(): Promise<{ projects: AdminProject[] }> {
  const response = await apiClient.get<{ success: boolean; data: { projects: AdminProject[] } }>('/admin/projects');
  return response.data;
}

/**
 * Get a single project by ID
 */
export async function getAdminProject(id: string): Promise<AdminProject> {
  const response = await apiClient.get<{ success: boolean; data: AdminProject }>(`/admin/projects/${encodeURIComponent(id)}`);
  return response.data;
}

export interface AdminProjectStats {
  team_members: number;
  collections: number;
  total_holders: number;
  xrp_deals_users: number;
  copied_listings_users: number;
  total_nfts: number;
  listed_nfts: number;
  deals_best_price_nfts: number;
}

/**
 * Get aggregate stats for a project (holders, users, listing coverage, etc.)
 */
export async function getAdminProjectStats(id: string): Promise<AdminProjectStats> {
  const response = await apiClient.get<{ success: boolean; data: AdminProjectStats }>(`/admin/projects/${encodeURIComponent(id)}/stats`);
  return response.data;
}

export interface AdminProjectSummaryStats {
  total_holders: number;
  total_nfts: number;
  listed_nfts: number;
  xrp_deals_users: number;
  copied_listings_users: number;
}

/**
 * Get lightweight stats for all projects, keyed by project id.
 * Omits the expensive "best price on deals" JSONB check.
 */
export async function getAdminProjectsStatsSummary(): Promise<Record<string, AdminProjectSummaryStats>> {
  const response = await apiClient.get<{ success: boolean; data: Record<string, AdminProjectSummaryStats> }>(
    '/admin/projects/stats-summary'
  );
  return response.data;
}

/**
 * Create a new project
 */
export async function createAdminProject(project: CreateProjectRequest): Promise<AdminProject> {
  const response = await apiClient.post<{ success: boolean; data: AdminProject }>('/admin/projects', project);
  return response.data;
}

/**
 * Update a project
 */
export async function updateAdminProject(id: string, project: UpdateProjectRequest): Promise<AdminProject> {
  const response = await apiClient.put<{ success: boolean; data: AdminProject }>(`/admin/projects/${encodeURIComponent(id)}`, project);
  return response.data;
}

/**
 * Delete a project
 */
export async function deleteAdminProject(id: string): Promise<void> {
  await apiClient.delete(`/admin/projects/${encodeURIComponent(id)}`);
}

/**
 * Add a token to a project
 */
export async function addProjectToken(projectId: string, token: AddTokenRequest): Promise<AdminProjectToken> {
  const response = await apiClient.post<{ success: boolean; data: AdminProjectToken }>(
    `/admin/projects/${encodeURIComponent(projectId)}/tokens`,
    token
  );
  return response.data;
}

/**
 * Remove a token from a project
 */
export async function removeProjectToken(projectId: string, tokenId: string): Promise<void> {
  await apiClient.delete(`/admin/projects/${encodeURIComponent(projectId)}/tokens/${encodeURIComponent(tokenId)}`);
}

/**
 * Manually refresh token data (price, market cap, social metrics)
 */
export async function refreshProjectToken(
  projectId: string,
  tokenId: string
): Promise<{
  price_xrp: number | null;
  market_cap_usd: number | null;
  holder_count: number | null;
  trustline_count: number | null;
  volume_24h_xrp: number | null;
  amm_liquidity_usd: number | null;
  last_updated: string;
}> {
  const response = await apiClient.post<{
    success: boolean;
    message: string;
    data: {
      price_xrp: number | null;
      market_cap_usd: number | null;
      holder_count: number | null;
      trustline_count: number | null;
      volume_24h_xrp: number | null;
      amm_liquidity_usd: number | null;
      last_updated: string;
    };
  }>(`/admin/projects/${encodeURIComponent(projectId)}/tokens/${encodeURIComponent(tokenId)}/refresh`, {});
  return response.data;
}

/**
 * Upload project image (logo or banner)
 */
export async function uploadProjectImage(
  projectId: string,
  file: File,
  type: 'image' | 'banner'
): Promise<{ url: string; type: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/admin/projects/${encodeURIComponent(projectId)}/upload-image?type=${type}`,
    {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || 'Failed to upload image');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get all tags
 */
export async function getAdminTags(): Promise<{ tags: AdminProjectTag[] }> {
  const response = await apiClient.get<{ success: boolean; data: { tags: AdminProjectTag[] } }>('/admin/tags');
  return response.data;
}

/**
 * Create a new tag
 */
export async function createAdminTag(tag: CreateTagRequest): Promise<AdminProjectTag> {
  const response = await apiClient.post<{ success: boolean; data: AdminProjectTag }>('/admin/tags', tag);
  return response.data;
}

/**
 * Delete a tag
 */
export async function deleteAdminTag(tagId: string): Promise<void> {
  await apiClient.delete(`/admin/tags/${encodeURIComponent(tagId)}`);
}

/**
 * Get projects for dropdown selection
 */
export async function getProjectsDropdown(): Promise<{ projects: ProjectDropdownItem[] }> {
  const response = await apiClient.get<{ success: boolean; data: { projects: ProjectDropdownItem[] } }>('/admin/projects/dropdown');
  return response.data;
}

/**
 * Get collections not assigned to any project
 */
export async function getUnassignedCollections(): Promise<{ collections: UnassignedCollection[] }> {
  const response = await apiClient.get<{ success: boolean; data: { collections: UnassignedCollection[] } }>('/admin/collections/unassigned');
  return response.data;
}

/**
 * Assign a collection to a project
 */
export async function assignCollectionToProject(collectionId: string, projectId: string | null): Promise<void> {
  await apiClient.put(`/admin/collections/${encodeURIComponent(collectionId)}/project`, { project_id: projectId });
}

/**
 * Update the display order of collections within a project
 */
export async function updateProjectCollectionOrder(projectId: string, collectionIds: string[]): Promise<void> {
  await apiClient.put(`/admin/projects/${encodeURIComponent(projectId)}/collection-order`, { collection_ids: collectionIds });
}

// ==================== Marketplace Analytics ====================

/**
 * Get marketplace analytics including 24hr and all-time volumes and fee revenue
 */
export async function getMarketplaceStats(): Promise<MarketplaceStatsResponse> {
  return apiClient.get<MarketplaceStatsResponse>('/admin/marketplaces/stats');
}

// ==================== Marketplace Backfill ====================

export interface MarketplaceBackfillStatusResponse {
  summary: {
    totalSales: number;
    salesWithMarketplace: number;
    salesWithoutMarketplace: number;
    collectionsNeedingBackfill: number;
  };
  collections: Array<{
    collectionId: string;
    name: string | null;
    creatorAddress: string;
    taxon: number;
    transactionsNeedingBackfill: number;
    hasActiveJob: boolean;
  }>;
  generatedAt: string;
}

export interface MarketplaceBackfillTriggerResponse {
  message: string;
  jobsCreated: number;
  jobs?: Array<{
    collectionId: string;
    jobId: string;
    name: string | null;
  }>;
}

/**
 * Get status of marketplace backfill - shows collections needing history re-fetch
 */
export async function getMarketplaceBackfillStatus(): Promise<MarketplaceBackfillStatusResponse> {
  return apiClient.get<MarketplaceBackfillStatusResponse>('/admin/marketplaces/backfill/status');
}

/**
 * Trigger marketplace backfill for collections
 * @param collectionId - Optional specific collection to backfill
 * @param limit - Max collections to queue (default 10, max 50)
 */
export async function triggerMarketplaceBackfill(
  collectionId?: string,
  limit?: number
): Promise<MarketplaceBackfillTriggerResponse> {
  return apiClient.post<MarketplaceBackfillTriggerResponse>('/admin/marketplaces/backfill', {
    collectionId,
    limit,
  });
}

// ==================== Launch Management ====================

/**
 * Launch status enum
 */
export type LaunchStatus =
  | 'draft'
  | 'pending_approval'
  | 'pending_authorization'
  | 'pending_payment'
  | 'processing'
  | 'ready'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

/**
 * Admin launch with creator info
 */
export interface AdminLaunch {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  creator_address: string;
  creator_user_id: string | null;
  creator_username: string | null;
  creator_profile_image_url: string | null;
  status: LaunchStatus;
  total_supply: number | null;
  minted_count: number;
  total_items: number;
  allowlist_count: number;
  thumbnail_url: string | null;
  banner_url: string | null;
  website_url: string | null;
  twitter_handle: string | null;
  discord_url: string | null;
  transfer_fee: number;
  mint_price_drops: number | null;
  allowlist_mint_price_drops: number | null;
  limit_per_wallet: number | null;
  allowlist_enabled: boolean;
  allowlist_2_enabled: boolean;
  allowlist_2_mint_price_drops: number | null;
  allowlist_start_at: string | null;
  allowlist_2_start_at: string | null;
  public_start_at: string | null;
  end_at: string | null;
  fee_paid: boolean;
  fee_payment_tx_hash: string | null;
  fee_amount_drops: number | null;
  admin_approved: boolean;
  admin_approved_at: string | null;
  admin_approved_by: string | null;
  admin_rejection_reason: string | null;
  // Composable fields
  collection_type: 'standard' | 'composable' | 'edition';
  base_image_url: string | null;
  composition_fee_per_nft_drops: number;
  compose_enabled: boolean;
  total_composed_count: number;
  total_burned_count: number;
  compose_revenue_drops: number;
  // IPFS upload status
  ipfs_uploaded: boolean;
  // Error tracking fields (from Launch model)
  consecutive_errors: number;
  last_error_at: string | null;
  auto_paused_at: string | null;
  auto_pause_reason: string | null;
  // Settlement settings
  auto_settle: boolean;
  // Service fee
  service_fee_percent: number;
  // Referral program
  referral_enabled: boolean;
  referral_bps: number | null;
  // Price locking
  public_phase_price_locked: boolean;
  // Claim-only minting (per-copy claim links)
  claim_only: boolean;
  // Visibility
  hidden: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Sample item for launch review
 */
export interface AdminLaunchItem {
  id: string;
  token_number: number;
  name: string | null;
  description: string | null;
  original_image_key: string | null;
  thumbnail_key: string | null;
  attributes: unknown;
  status: string;
  image_cid: string | null;
  metadata_uri: string | null;
}

/**
 * Detailed launch item for items tab
 */
export interface AdminLaunchItemDetail {
  id: string;
  token_number: number;
  name: string | null;
  status: string;
  nft_token_id: string | null;
  mint_tx_hash: string | null;
  minted_at: string | null;
  transfer_offer_id: string | null;
  transferred_at: string | null;
  reserved_for_address: string | null;
  reserved_at: string | null;
  error_message: string | null;
  original_image_key: string | null;
}

/**
 * Detailed launch mint for mints tab
 */
export interface AdminLaunchMint {
  id: string;
  batch_id: string;
  buyer_address: string;
  status: string;
  mint_price_drops: number;
  payment_tx_hash: string | null;
  payment_confirmed_at: string | null;
  nft_token_id: string | null;
  mint_tx_hash: string | null;
  minted_at: string | null;
  transfer_offer_id: string | null;
  transfer_tx_hash: string | null;
  transferred_at: string | null;
  error_message: string | null;
  retry_count: number;
  requires_mint_retry: boolean;
  requires_recovery: boolean;
  requires_admin_review: boolean;
  failed_at: string | null;
  created_at: string;
}

/**
 * Launch settlement for settlements tab
 */
export interface AdminLaunchSettlement {
  id: string;
  creator_address: string;
  amount_drops: number;
  mint_count: number;
  status: string;
  tx_hash: string | null;
  ledger_index: number | null;
  error_message: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  completed_at: string | null;
}

/**
 * Launch detail with sample items
 */
export interface AdminTraitCategory {
  id: string;
  name: string;
  z_index: number;
  values: Array<{
    id: string;
    name: string;
    image_url: string | null;
    total_supply: number;
    minted_count: number;
    remaining_count: number;
  }>;
}

export interface AdminLaunchDetail extends AdminLaunch {
  sample_items: AdminLaunchItem[];
  fee_xrp?: number;
  // IPFS upload progress (standard launches only)
  ipfs_progress: {
    total: number;
    completed: number;
    failed: number;
  } | null;
  // Error tracking for admin visibility
  consecutive_errors: number;
  last_error_at: string | null;
  auto_paused_at: string | null;
  auto_pause_reason: string | null;
  mints_requiring_refund: number;
  failed_mints_count: number;
  // Composable trait categories (only for composable launches)
  trait_categories?: AdminTraitCategory[];
  // Edition artworks (only for edition launches) — one entry per artwork
  editions?: AdminLaunchEdition[];
}

export interface AdminLaunchEdition {
  id: string;
  edition_number: number;
  name: string;
  image_url: string | null;
  max_supply: number;
  minted_count: number;
  available_count: number;
  price_drops: number | null;
}

/**
 * Response for listing launches
 */
export interface AdminLaunchesListResponse {
  launches: AdminLaunch[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  statusCounts: Record<string, number>;
}

/**
 * Get all launches with filtering
 */
export async function getAdminLaunches(params?: {
  page?: number;
  limit?: number;
  status?: LaunchStatus | 'all';
  creator?: string;
}): Promise<AdminLaunchesListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params?.creator) queryParams.append('creator', params.creator);

  const query = queryParams.toString();
  const endpoint = `/admin/launches${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{ success: boolean; data: AdminLaunchesListResponse }>(endpoint);
  return response.data;
}

/**
 * Get launch detail for review
 */
export async function getAdminLaunchDetail(id: string): Promise<AdminLaunchDetail> {
  const response = await apiClient.get<{ success: boolean; data: AdminLaunchDetail }>(`/admin/launches/${id}`);
  return response.data;
}

/**
 * Approve a launch
 */
export async function approveLaunch(id: string): Promise<AdminLaunch> {
  const response = await apiClient.post<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/approve`);
  return response.data;
}

/**
 * Reject a launch with reason
 */
export async function rejectLaunch(id: string, reason: string): Promise<AdminLaunch> {
  const response = await apiClient.post<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/reject`, { reason });
  return response.data;
}

/**
 * Get count of launches pending approval (for nav badge)
 */
export async function getPendingLaunchesCount(): Promise<number> {
  const response = await apiClient.get<{ success: boolean; data: { count: number } }>('/admin/launches/pending-count');
  return response.data.count;
}

// ==================== Settlement Types ====================

export type SettlementStatus = 'pending' | 'approved' | 'processing' | 'completed' | 'failed' | 'failed_unverified';

export type SettlementType = 'mint_revenue' | 'composition_fee' | 'registration_revenue';

export interface AdminSettlement {
  id: string;
  launch_id: string;
  creator_address: string;
  amount_drops: number;
  amount_xrp: number;
  mint_count: number;
  mint_ids: string[];
  settlement_type: SettlementType;
  status: SettlementStatus;
  tx_hash: string | null;
  ledger_index: number | null;
  error_message: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  completed_at: string | null;
  // Joined fields
  launch_name?: string;
  launch_slug?: string;
  launch_thumbnail_url?: string | null;
}

export interface SettlementMint {
  id: string;
  buyer_address: string;
  payment_amount_drops: number;
  payment_tx_hash: string | null;
  nft_token_id: string | null;
  mint_tx_hash: string | null;
  transfer_offer_id: string | null;
  item_name: string | null;
  referral_fee_drops: number;
  status: string;
  created_at: string;
  completed_at: string | null;
  // Registration payment fields (present when settlement_type === 'registration_revenue')
  phase?: string;
  quantity?: number;
  price_per_slot_drops?: string;
  total_base_drops?: string;
  platform_fee_drops?: string;
  confirmed_at?: string | null;
}

export interface SettlementVerificationData extends AdminSettlement {
  launch_status?: string;
  launch_total_supply?: number | null;
  launch_minted_count?: number;
  launch_mint_price_drops?: number | null;
  launch_mint_price_xrp?: number | null;
  total_payments_received_drops: number;
  total_payments_received_xrp: number;
  platform_fee_collected_drops: number;
  platform_fee_collected_xrp: number;
  mints: SettlementMint[];
  verification: {
    payment_verified: boolean;
    mint_count_verified: boolean;
    all_mints_completed: boolean;
  };
}

export interface AdminSettlementsListResponse {
  settlements: AdminSettlement[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ==================== Settlement API Functions ====================

/**
 * Get pending settlements for approval
 */
export async function getAdminPendingSettlements(params?: {
  page?: number;
  limit?: number;
}): Promise<AdminSettlementsListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const query = queryParams.toString();
  const endpoint = `/admin/settlements${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{ success: boolean; data: AdminSettlementsListResponse }>(endpoint);
  return response.data;
}

/**
 * Get recent settlements (all statuses) for monitoring
 */
export async function getAdminRecentSettlements(): Promise<{ settlements: AdminSettlement[] }> {
  const response = await apiClient.get<{ success: boolean; data: { settlements: AdminSettlement[] } }>('/admin/settlements/recent');
  return response.data;
}

/**
 * Get settlement verification data for admin review
 */
export async function getAdminSettlementVerification(id: string): Promise<SettlementVerificationData> {
  const response = await apiClient.get<{ success: boolean; data: SettlementVerificationData }>(`/admin/settlements/${id}`);
  return response.data;
}

/**
 * Approve a settlement for payment processing
 */
export async function approveSettlement(id: string): Promise<AdminSettlement> {
  const response = await apiClient.post<{ success: boolean; message: string; data: AdminSettlement }>(`/admin/settlements/${id}/approve`);
  return response.data;
}

/**
 * Mark a failed/failed_unverified settlement as paid with an on-chain tx hash
 */
export async function markSettlementPaid(id: string, txHash: string): Promise<void> {
  await apiClient.post<{ success: boolean; message: string }>(`/admin/settlements/${id}/mark-paid`, { tx_hash: txHash });
}

/**
 * Get count of settlements pending approval (for nav badge)
 */
export async function getPendingSettlementsCount(): Promise<number> {
  const response = await apiClient.get<{ success: boolean; data: { count: number } }>('/admin/settlements/pending-count');
  return response.data.count;
}

// ==================== Failed Registration Payments ====================

export interface FailedRegistrationPayment {
  id: string;
  launch_id: string;
  buyer_address: string;
  phase: string;
  quantity: number;
  payment_amount_drops: number;
  payment_amount_xrp: number;
  tx_hash: string;
  error_message: string | null;
  created_at: string;
  launch_name: string;
  launch_slug: string;
}

/**
 * Get failed registration payments that may need refunds
 * (on-chain payment made but registration couldn't be applied)
 */
export async function getFailedRegistrationPayments(): Promise<{ payments: FailedRegistrationPayment[]; count: number }> {
  const response = await apiClient.get<{ success: boolean; data: { payments: FailedRegistrationPayment[]; count: number } }>('/admin/settlements/failed-registration-payments');
  return response.data;
}

// ==================== Launch Admin Actions ====================

/**
 * Resume an auto-paused launch
 */
export async function resumeAutoPausedLaunch(id: string): Promise<AdminLaunch> {
  const response = await apiClient.post<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/resume`);
  return response.data;
}

/**
 * Enable or disable automatic settlement approval for a launch
 */
export async function setAutoSettle(id: string, enabled: boolean): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/auto-settle`, { enabled });
  return response.data;
}

// ==================== Claim Tokens (per-copy claim links) ====================
// See docs/features/LAUNCH_CLAIM_TOKENS.md

export interface AdminClaimToken {
  id: string;
  launch_id: string;
  item_id: string;
  /** First 8 chars only — the token itself is stored hashed and unrecoverable */
  token_prefix: string;
  status: 'unclaimed' | 'claimed' | 'void';
  claimed_by_address: string | null;
  claimed_at: string | null;
  void_reason: string | null;
  item_name: string | null;
  item_status: string;
  edition_serial: number | null;
  edition_name: string | null;
  nft_token_id: string | null;
  /** Status of the mint this claim produced, if any */
  mint_status: string | null;
  mint_error: string | null;
  mint_failed_at: string | null;
  /** Link was spent but the mint failed and nothing exists on-chain */
  needs_reset: boolean;
}

export interface AdminClaimTokenList {
  claim_only: boolean;
  stats: {
    total: number;
    unclaimed: number;
    claimed: number;
    void: number;
    stranded: number;
    delivered: number;
  };
  tokens: AdminClaimToken[];
}

export async function getLaunchClaimTokens(id: string): Promise<AdminClaimTokenList> {
  const response = await apiClient.get<{ success: boolean; data: AdminClaimTokenList }>(
    `/admin/launches/${id}/claim-tokens`
  );
  return response.data;
}

export interface GeneratedClaimLinks {
  created: number;
  stats: AdminClaimTokenList['stats'];
  /** Plaintext links — present ONLY in this response, never retrievable again */
  links: Array<{ serial: number | null; claim_url: string }>;
  csv: string;
  warning: string;
}

/**
 * Idempotent: creates a token for every item that lacks a live one.
 *
 * ⚠️ The returned `links`/`csv` are the only copy of the plaintext claim URLs.
 * Tokens are stored hashed, so anything not saved from this response is gone.
 */
export async function generateLaunchClaimTokens(id: string): Promise<GeneratedClaimLinks> {
  const response = await apiClient.post<{ success: boolean; data: GeneratedClaimLinks }>(
    `/admin/launches/${id}/claim-tokens/generate`
  );
  return response.data;
}

/** Save a generated CSV to disk client-side — the server never stores it. */
export function downloadClaimLinksCsv(csv: string, launchSlug: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `claim-links-${launchSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function voidLaunchClaimToken(tokenId: string, reason: string): Promise<void> {
  await apiClient.post(`/admin/launches/claim-tokens/${tokenId}/void`, { reason });
}

/**
 * Recover a stranded claim (link spent, mint failed, nothing on-chain).
 * Voids the token and frees the piece so Generate can issue a replacement.
 * Server refuses unless the mint is definitively 'failed' with no NFT.
 */
export async function resetLaunchClaimToken(
  tokenId: string,
  reason: string
): Promise<{ item_id: string; serial: number | null }> {
  const response = await apiClient.post<{ success: boolean; data: { item_id: string; serial: number | null } }>(
    `/admin/launches/claim-tokens/${tokenId}/reset`,
    { reason }
  );
  return response.data;
}

/**
 * Download the serial -> claim URL CSV. Uses fetch rather than a plain <a href>
 * because admin routes need the Authorization header.
 */
export async function exportClaimTokensCsv(id: string): Promise<void> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

  const response = await fetch(`${API_BASE_URL}/api/v1/admin/launches/${id}/claim-tokens/export`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to export claim tokens');
  }

  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'claim-tokens.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export async function setLaunchClaimOnly(id: string, claimOnly: boolean): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(
    `/admin/launches/${id}/claim-only`,
    { claim_only: claimOnly }
  );
  return response.data;
}

/**
 * Admin override of referral config on a launch
 */
export async function setLaunchReferralConfig(
  id: string,
  data: { referral_enabled: boolean; referral_bps?: number | null }
): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/referrals`, data);
  return response.data;
}

/**
 * Show or hide a launch from the public drops listing page
 */
export async function setLaunchHidden(id: string, hidden: boolean): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/hidden`, { hidden });
  return response.data;
}

/**
 * Enable or disable public phase price locking for a launch
 */
export async function setPublicPhasePriceLocked(id: string, locked: boolean): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(`/admin/launches/${id}/public-phase-price-locked`, { locked });
  return response.data;
}

/**
 * Set the mint service fee percentage for a launch
 */
export async function setLaunchServiceFee(id: string, percentage: number): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(
    `/admin/launches/${id}/service-fee`,
    { percentage }
  );
  return response.data;
}

/**
 * Update social links (website, twitter, discord) for a launch
 */
export async function updateLaunchSocialLinks(
  id: string,
  updates: { website_url?: string | null; twitter_handle?: string | null; discord_url?: string | null }
): Promise<AdminLaunch> {
  const response = await apiClient.patch<{ success: boolean; data: AdminLaunch }>(
    `/admin/launches/${id}/social-links`,
    updates
  );
  return response.data;
}

// ==================== Refund Management ====================

/**
 * Mint requiring refund
 */
export interface AdminRefundMint {
  id: string;
  buyer_address: string;
  mint_price_drops: number;
  error_message: string | null;
  failed_at: string | null;
  payment_tx_hash: string | null;
  refund_processed_at: string | null;
  refund_processed_by: string | null;
  refund_notes: string | null;
  refund_tx_hash: string | null;
}

/**
 * Get mints requiring refund for a launch
 */
export async function getAdminLaunchRefunds(launchId: string): Promise<{
  mints: AdminRefundMint[];
  total: number;
  unprocessed: number;
}> {
  const response = await apiClient.get<{
    success: boolean;
    data: { mints: AdminRefundMint[]; total: number; unprocessed: number };
  }>(`/admin/launches/${launchId}/refunds`);
  return response.data;
}

/**
 * Mark a refund as processed
 */
export async function processAdminRefund(
  launchId: string,
  mintId: string,
  options?: { notes?: string; refund_tx_hash?: string }
): Promise<{ id: string; refund_processed_at: string; refund_processed_by: string; refund_tx_hash: string | null }> {
  const response = await apiClient.post<{
    success: boolean;
    data: { id: string; refund_processed_at: string; refund_processed_by: string; refund_tx_hash: string | null };
  }>(`/admin/launches/${launchId}/refunds/${mintId}/process`, options || {});
  return response.data;
}

// ==================== Mint Resolution ====================

export interface ResolveMintRequest {
  action: 'retry' | 'complete' | 'mark_failed';
  mint_tx_hash?: string;
  nft_token_id?: string;
  transfer_offer_id?: string;
  transfer_tx_hash?: string;
  error_message?: string;
  requires_refund?: boolean;
  notes?: string;
}

export interface BulkResolveMintRequest {
  mint_ids: string[];
  action: 'retry' | 'mark_failed';
  error_message?: string;
  requires_refund?: boolean;
  notes?: string;
}

export interface BulkResolveResult {
  succeeded: string[];
  failed: Array<{ mintId: string; error: string }>;
}

/**
 * Resolve a stuck mint (retry, complete with on-chain data, or mark failed)
 */
export async function resolveAdminMint(
  launchId: string,
  mintId: string,
  data: ResolveMintRequest
): Promise<{ action: string; previous_status?: string; requires_refund?: boolean }> {
  const response = await apiClient.post<{
    success: boolean;
    data: { action: string; previous_status?: string; requires_refund?: boolean };
  }>(`/admin/launches/${launchId}/mints/${mintId}/resolve`, data);
  return response.data;
}

/**
 * Bulk resolve multiple stuck mints
 */
export async function bulkResolveAdminMints(
  launchId: string,
  data: BulkResolveMintRequest
): Promise<BulkResolveResult> {
  const response = await apiClient.post<{
    success: boolean;
    data: BulkResolveResult;
  }>(`/admin/launches/${launchId}/mints/bulk-resolve`, data);
  return response.data;
}

// ==================== Launch Detail Tabs (Items, Mints, Settlements) ====================

/**
 * Get paginated items for a launch
 */
export async function getAdminLaunchItems(launchId: string, params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<{
  items: AdminLaunchItemDetail[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  statusCounts: Record<string, number>;
}> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);

  const query = queryParams.toString();
  const endpoint = `/admin/launches/${launchId}/items${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{
    success: boolean;
    data: {
      items: AdminLaunchItemDetail[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
      statusCounts: Record<string, number>;
    };
  }>(endpoint);
  return response.data;
}

/**
 * Get paginated mints for a launch
 */
export async function getAdminLaunchMints(launchId: string, params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<{
  mints: AdminLaunchMint[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  statusCounts: Record<string, number>;
}> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);

  const query = queryParams.toString();
  const endpoint = `/admin/launches/${launchId}/mints${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{
    success: boolean;
    data: {
      mints: AdminLaunchMint[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
      statusCounts: Record<string, number>;
    };
  }>(endpoint);
  return response.data;
}

/**
 * Get settlements for a launch (no pagination)
 */
export async function getAdminLaunchSettlements(launchId: string): Promise<{
  settlements: AdminLaunchSettlement[];
}> {
  const response = await apiClient.get<{
    success: boolean;
    data: { settlements: AdminLaunchSettlement[] };
  }>(`/admin/launches/${launchId}/settlements`);
  return response.data;
}

// ==================== Launchpad Analytics ====================

/**
 * Launchpad analytics data
 */
export interface LaunchpadAnalytics {
  // Overall stats
  totalLaunches: number;
  activeLaunches: number;
  completedLaunches: number;
  pausedLaunches: number;

  // Mint stats
  totalMints: number;
  successfulMints: number;
  failedMints: number;
  mintSuccessRate: number;

  // Revenue stats (in XRP)
  totalRevenueXrp: number;
  settledRevenueXrp: number;
  pendingSettlementXrp: number;

  // Refund stats
  mintsRequiringRefund: number;
  refundsProcessed: number;

  // Recent activity (last 7 days)
  recentMints: number;
  recentRevenueXrp: number;

  // Top launches by revenue
  topLaunches: Array<{
    id: string;
    name: string;
    slug: string;
    totalRevenueXrp: number;
    mintedCount: number;
  }>;
}

/**
 * Get launchpad-wide analytics
 */
export async function getLaunchpadAnalytics(): Promise<LaunchpadAnalytics> {
  const response = await apiClient.get<{ success: boolean; data: LaunchpadAnalytics }>('/admin/launches/analytics');
  return response.data;
}

// ==================== Launchpad Health ====================

export interface LaunchpadHealthData {
  overallStatus: 'healthy' | 'degraded' | 'critical';
  serviceReachable: boolean;
  service: {
    status: string;
    checks: {
      database: boolean;
      xrpl: {
        connected: boolean;
        url: string | null;
        lastConnectedAt: string | null;
        lastDisconnectedAt: string | null;
        reconnectAttempts: number;
        reconnecting: boolean;
      };
      mintProcessor: {
        running: boolean;
        activeWorkers: number;
        maxConcurrency: number;
        consecutivePollErrors: number;
        lastSuccessfulPollAt: string | null;
        lastPollErrorMessage: string | null;
        circuitBreakerOpen: boolean;
      } | null;
    };
  } | null;
  pipeline: {
    paymentPending: number;
    paymentConfirmed: number;
    minting: number;
    minted: number;
    transferring: number;
    completed24h: number;
    failed24h: number;
    expired24h: number;
    stuckMints: number;
    avgProcessingSeconds: number | null;
  };
  settlements: {
    pending: number;
    failed: number;
    lastCompletedAt: string | null;
  };
  recentErrors: Array<{
    message: string;
    count: number;
  }>;
}

export async function getLaunchpadHealth(): Promise<LaunchpadHealthData> {
  const response = await apiClient.get<{ success: boolean; data: LaunchpadHealthData }>('/admin/launches/health');
  return response.data;
}

// ==================== Unmatched Payments ====================

export interface UnmatchedPayment {
  id: string;
  tx_hash: string;
  sender_address: string;
  destination_address: string;
  amount_drops: string;
  memo_data: string | null;
  memo_type: string | null;
  ledger_index: string | null;
  validated_at: string | null;
  detected_at: string;
  processing_status: 'unmatched' | 'refunded';
  refund_tx_hash: string | null;
  refund_processed_at: string | null;
  refund_processed_by: string | null;
  refund_notes: string | null;
}

export interface UnmatchedPaymentsResponse {
  payments: UnmatchedPayment[];
  unresolved: number;
}

export async function getUnmatchedPayments(opts?: {
  minAgeSeconds?: number;
  includeResolved?: boolean;
}): Promise<UnmatchedPaymentsResponse> {
  const params = new URLSearchParams();
  if (opts?.minAgeSeconds !== undefined) params.append('min_age_seconds', String(opts.minAgeSeconds));
  if (opts?.includeResolved) params.append('include_resolved', 'true');
  const qs = params.toString();
  const response = await apiClient.get<{ success: boolean; data: UnmatchedPaymentsResponse }>(
    `/admin/launches/unmatched-payments${qs ? `?${qs}` : ''}`
  );
  return response.data;
}

export async function resolveUnmatchedPayment(
  id: string,
  body: { refund_tx_hash?: string; notes?: string }
): Promise<UnmatchedPayment> {
  const response = await apiClient.post<{ success: boolean; data: UnmatchedPayment }>(
    `/admin/launches/unmatched-payments/${id}/resolve`,
    body
  );
  return response.data;
}

// ==================== Trade Management ====================

/**
 * Get trades list with filters and pagination
 */
export async function getAdminTrades(params?: {
  page?: number;
  limit?: number;
  status?: string;
  address?: string;
  sort?: 'created_at' | 'updated_at' | 'expires_at' | 'status';
  order?: 'asc' | 'desc';
}): Promise<AdminTradesListResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params?.address) queryParams.append('address', params.address);
  if (params?.sort) queryParams.append('sort', params.sort);
  if (params?.order) queryParams.append('order', params.order);

  const query = queryParams.toString();
  const endpoint = `/admin/trades${query ? `?${query}` : ''}`;

  const response = await apiClient.get<{ success: boolean; data: AdminTradesListResponse }>(endpoint);
  return response.data;
}

/**
 * Get trade summary statistics
 */
export async function getAdminTradeStats(): Promise<AdminTradeStats> {
  const response = await apiClient.get<{ success: boolean; data: AdminTradeStats }>('/admin/trades/stats');
  return response.data;
}

/**
 * Get full trade detail
 */
export async function getAdminTradeDetail(id: string): Promise<AdminTradeDetailResponse> {
  const response = await apiClient.get<{ success: boolean; data: AdminTradeDetailResponse }>(`/admin/trades/${encodeURIComponent(id)}`);
  return response.data;
}

/**
 * Export all mints requiring refund as CSV
 * Triggers a file download
 */
export async function exportRefundsCsv(): Promise<void> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

  const response = await fetch(`${API_BASE_URL}/api/v1/admin/launches/refunds/export`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to export refunds');
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'launchpad-refunds.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ==================== Referral Payouts ====================

export interface AdminReferralPayout {
  id: string;
  user_id: string;
  payout_address: string;
  amount_drops: number;
  status: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_reason: string | null;
  tx_hash: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  username?: string | null;
  xrpl_address?: string;
}

export interface ReferralLiabilityStats {
  total_accrued_drops: number;
  total_paid_drops: number;
  committed_drops: number;
  outstanding_drops: number;
  uncommitted_drops: number;
  pending_requests: number;
  queued_payouts: number;
  failed_payouts: number;
}

export async function getAdminReferralPayoutStats(): Promise<ReferralLiabilityStats> {
  const response = await apiClient.get<{ success: boolean } & ReferralLiabilityStats>(
    '/admin/referral-payouts/stats'
  );
  return response;
}

export async function getAdminReferralPayouts(params?: {
  page?: number;
  limit?: number;
}): Promise<{ payouts: AdminReferralPayout[]; total: number }> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  const query = queryParams.toString();
  const response = await apiClient.get<{
    success: boolean;
    payouts: AdminReferralPayout[];
    total: number;
  }>(`/admin/referral-payouts${query ? `?${query}` : ''}`);
  return { payouts: response.payouts, total: response.total };
}

export async function getAdminReferralPayoutDetail(id: string): Promise<{
  payout: AdminReferralPayout;
  referrer: { username: string | null; xrpl_address: string } | null;
  balance: any;
  earnings: any[];
}> {
  const response = await apiClient.get<{
    success: boolean;
    payout: AdminReferralPayout;
    referrer: any;
    balance: any;
    earnings: any[];
  }>(`/admin/referral-payouts/${id}`);
  return response;
}

export async function approveReferralPayout(id: string): Promise<AdminReferralPayout> {
  const response = await apiClient.post<{
    success: boolean;
    payout: AdminReferralPayout;
  }>(`/admin/referral-payouts/${id}/approve`);
  return response.payout;
}

export async function rejectReferralPayout(id: string, reason: string): Promise<AdminReferralPayout> {
  const response = await apiClient.post<{
    success: boolean;
    payout: AdminReferralPayout;
  }>(`/admin/referral-payouts/${id}/reject`, { reason });
  return response.payout;
}

export async function retryReferralPayout(id: string): Promise<AdminReferralPayout> {
  const response = await apiClient.post<{
    success: boolean;
    payout: AdminReferralPayout;
  }>(`/admin/referral-payouts/${id}/retry`);
  return response.payout;
}
